/**
 * The catalog LOOKUP API, free of any content import (type-only imports), so
 * it is safe for the browser bundle.
 *
 * Two producers share it, which keeps their semantics identical by
 * construction:
 *   - `getCatalog(locale)` in `./localized.ts` (server, scripts, tests): the
 *     full locale-resolved catalog;
 *   - `catalogFromSnapshot()` in `./catalog-context.tsx` (browser + SSR of a
 *     page): the slice of the catalog a page's server loader sent down
 *     (`./page-data.ts`). Content never ships inside the JS bundle (M6).
 */
import type {
  Article,
  Build,
  ChangelogEntry,
  CommunityPost,
  Comparison,
  Course,
  Fundamental,
  GlossaryTerm,
  Guide,
  Lesson,
  Resource,
  Tool,
  communityTopics,
} from './catalog'
import type { Locale } from '../lib/i18n/config'

export type CommunityTopic = (typeof communityTopics)[number]

export type CatalogCollections = {
  courses: Course[]
  lessons: Lesson[]
  resources: Resource[]
  articles: Article[]
  fundamentals: Fundamental[]
  builds: Build[]
  glossaryTerms: GlossaryTerm[]
  comparisons: Comparison[]
  guides: Guide[]
  tools: Tool[]
  communityTopics: CommunityTopic[]
  communityPosts: CommunityPost[]
  changelogEntries: ChangelogEntry[]
}

export type CollectionKey = keyof CatalogCollections

export const COLLECTION_KEYS: ReadonlyArray<CollectionKey> = [
  'courses',
  'lessons',
  'resources',
  'articles',
  'fundamentals',
  'builds',
  'glossaryTerms',
  'comparisons',
  'guides',
  'tools',
  'communityTopics',
  'communityPosts',
  'changelogEntries',
]

export type Catalog = CatalogCollections & {
  locale: Locale
  getCourse: (slug: string) => Course | undefined
  getLesson: (courseSlug: string, lessonSlug: string) => Lesson | undefined
  getLessonsForCourse: (courseSlug: string) => Lesson[]
  getResource: (slug: string) => Resource | undefined
  getArticle: (slug: string) => Article | undefined
  getFundamental: (slug: string) => Fundamental | undefined
  getBuild: (slug: string) => Build | undefined
  getGlossaryTerm: (slug: string) => GlossaryTerm | undefined
  getComparison: (slug: string) => Comparison | undefined
  getGuide: (slug: string) => Guide | undefined
  getTool: (slug: string) => Tool | undefined
  getCommunityPost: (slug: string) => CommunityPost | undefined
  getChangelogEntry: (slug: string) => ChangelogEntry | undefined
}

/** Attach the lookup methods to a set of (already localized) collections. */
export function assembleCatalog(
  locale: Locale,
  c: CatalogCollections,
): Catalog {
  return {
    locale,
    ...c,
    getCourse: (slug) => c.courses.find((x) => x.slug === slug),
    getLesson: (courseSlug, lessonSlug) =>
      c.lessons.find((l) => l.courseSlug === courseSlug && l.slug === lessonSlug),
    getLessonsForCourse: (courseSlug) =>
      c.lessons.filter((l) => l.courseSlug === courseSlug),
    getResource: (slug) => c.resources.find((x) => x.slug === slug),
    getArticle: (slug) => c.articles.find((x) => x.slug === slug),
    getFundamental: (slug) => c.fundamentals.find((x) => x.slug === slug),
    getBuild: (slug) => c.builds.find((x) => x.slug === slug),
    getGlossaryTerm: (slug) => c.glossaryTerms.find((x) => x.slug === slug),
    getComparison: (slug) => c.comparisons.find((x) => x.slug === slug),
    getGuide: (slug) => c.guides.find((x) => x.slug === slug),
    getTool: (slug) => c.tools.find((x) => x.slug === slug),
    getCommunityPost: (slug) => c.communityPosts.find((x) => x.slug === slug),
    getChangelogEntry: (slug) =>
      c.changelogEntries.find((x) => x.slug === slug),
  }
}
